import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { isEqual, omit } from 'lodash-es';
import { isUrlValid } from '~/utils/videoUrl/videoUrl';
export const MAX_CUSTOM_PAGES = 10;
export const DEFAULT_PAGE_TITLE = 'Custom page';
export const DEFAULT_DISPLAY_SCOPE = 'proposalOnly';
const BYTES_PER_MEGABYTE = 1024 * 1024;
export const PDF_MAX_SIZE_BYTES = 10 * BYTES_PER_MEGABYTE;
export const isProposalAndAgreement = (page)=>page.displayScope === 'proposalAndAgreement';
export const supportsDisplayScope = (page)=>page.type !== 'video';
export const isShownOnAgreement = (page)=>isProposalAndAgreement(page) && supportsDisplayScope(page);
export const sortedPages = (pages)=>[
        ...pages
    ].sort((a, b)=>a.displayOrder - b.displayOrder);
export const agreementPages = (pages)=>sortedPages(pages.filter(isShownOnAgreement));
export const reindexDisplayOrders = (pages)=>pages.map((page, index)=>_object_spread_props(_object_spread({}, page), {
            displayOrder: index
        }));
export const reorderVisibleKeepingHidden = (allPages, reorderedVisibleIds, hiddenPageIds)=>{
    const visibleById = new Map(allPages.filter((p)=>!hiddenPageIds.has(p.id)).map((p)=>[
            p.id,
            p
        ]));
    const reorderedVisible = reorderedVisibleIds.map((id)=>visibleById.get(id)).filter((p)=>p != null);
    const hidden = sortedPages(allPages.filter((p)=>hiddenPageIds.has(p.id)));
    return reindexDisplayOrders([
        ...reorderedVisible,
        ...hidden
    ]);
};
export function isCustomPageContentEdited(current, original) {
    if (original == null) return false;
    if (current.type === 'text' && original.type === 'text') {
        return current.text.textId !== original.text.textId;
    }
    if (current.type === 'pdf' && original.type === 'pdf') {
        return current.pdf.pdfName !== original.pdf.pdfName || current.pdf.pdfFileName !== original.pdf.pdfFileName;
    }
    return false;
}
export const isCustomPageEmpty = (page)=>{
    switch(page.type){
        case 'text':
            return page.text.textId === '' || page.text.isEmpty === true;
        case 'pdf':
            return page.pdf.pdfName === '' || page.pdf.pdfFileName === '';
        case 'video':
            return page.video.videoUrl === '';
    }
};
const hasFailedTextSave = (page)=>page.type === 'text' && page.text.saveFailed === true;
const hasUnsupportedVideoUrl = (page)=>page.type === 'video' && !isUrlValid(page.video.videoUrl);
const hasUnsavedTextContent = (page)=>page.type === 'text' && page.text.isEmpty === false && page.text.textId === '';
export const isCustomPageContentInvalid = (page, options)=>{
    if (isCustomPageEmpty(page)) {
        return (options === null || options === void 0 ? void 0 : options.allowEmpty) !== true || hasFailedTextSave(page) || hasUnsavedTextContent(page);
    }
    return hasFailedTextSave(page) || hasUnsupportedVideoUrl(page);
};
export const toCustomPageDTO = (page)=>{
    if (page.type !== 'text') return page;
    const _page_text = page.text, { isEmpty: _isEmpty, saveFailed: _saveFailed } = _page_text, text = _object_without_properties(_page_text, [
        "isEmpty",
        "saveFailed"
    ]);
    return _object_spread_props(_object_spread({}, page), {
        text
    });
};
export const isSameCustomPage = (a, b)=>isEqual(omit(toCustomPageDTO(a), 'displayOrder'), omit(toCustomPageDTO(b), 'displayOrder'));
export const createBlankVideoPage = (id, displayOrder)=>({
        id,
        pageTitle: DEFAULT_PAGE_TITLE,
        displayOrder,
        displayScope: DEFAULT_DISPLAY_SCOPE,
        type: 'video',
        video: {
            videoUrl: ''
        }
    });
export const createBlankTextPage = (id, displayOrder, textId)=>({
        id,
        pageTitle: DEFAULT_PAGE_TITLE,
        displayOrder,
        displayScope: DEFAULT_DISPLAY_SCOPE,
        type: 'text',
        text: {
            textId
        }
    });
export const createBlankPdfPage = (id, displayOrder, pdfName, pdfFileName)=>({
        id,
        pageTitle: DEFAULT_PAGE_TITLE,
        displayOrder,
        displayScope: DEFAULT_DISPLAY_SCOPE,
        type: 'pdf',
        pdf: {
            pdfName,
            pdfFileName
        }
    });
export const createEmptyPdfPage = (id, displayOrder)=>createBlankPdfPage(id, displayOrder, '', '');
